import { db } from '../firebase';
import firebase from '../firebase';

const QURAN_API_BASE = 'https://api.alquran.cloud/v1';

export const getSurahsFromFirestore = async () => {
  const snapshot = await db.collection('surahs').orderBy('number', 'asc').get();
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getSurahAyahsFromFirestore = async (surahNumber) => {
  const snapshot = await db.collection(`surahs/${surahNumber}/ayahs`).orderBy('numberInSurah', 'asc').get();
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const updateSurahMeta = async (surahNumber, data) => {
  await db.collection('surahs').doc(surahNumber.toString()).update(data);
};

export const updateAyah = async (surahNumber, ayahNumber, data) => {
  await db.collection(`surahs/${surahNumber}/ayahs`).doc(ayahNumber.toString()).update(data);
};

export const publishToApp = async () => {
  await db.collection('quran_meta').doc('info').set({
    last_published_time: firebase.firestore.FieldValue.serverTimestamp()
  }, { merge: true });
};

export const getTotalSurahs = async () => {
  const snapshot = await db.collection('surahs').get();
  return snapshot.size;
};

export const backupQuranData = async (onProgress) => {
  try {
    const timestamp = Date.now();
    const backupCollectionId = `surahs_backup_${timestamp}`;

    onProgress('Fetching current surahs for backup...');
    const surahsSnapshot = await db.collection('surahs').get();
    const totalSurahs = surahsSnapshot.docs.length;

    if (totalSurahs === 0) {
      onProgress('Nothing to backup.');
      return;
    }

    let processedCount = 0;

    for (const surahDoc of surahsSnapshot.docs) {
      const surahData = surahDoc.data();
      const surahNumber = surahDoc.id;

      // Save surah doc to backup
      await db.collection(backupCollectionId).doc(surahNumber).set(surahData);

      // Fetch and backup ayahs
      const ayahsSnapshot = await db.collection(`surahs/${surahNumber}/ayahs`).get();

      // Use batch for ayahs backup to speed things up
      let batch = db.batch();
      let opCount = 0;

      for (const ayahDoc of ayahsSnapshot.docs) {
        const ayahRef = db.collection(`${backupCollectionId}/${surahNumber}/ayahs`).doc(ayahDoc.id);
        batch.set(ayahRef, ayahDoc.data());
        opCount++;

        if (opCount >= 450) { // Firestore batch limit is 500
          await batch.commit();
          batch = db.batch();
          opCount = 0;
        }
      }

      if (opCount > 0) {
        await batch.commit();
      }

      processedCount++;
      onProgress(`Backed up Surah ${processedCount} of ${totalSurahs}`);
    }

    onProgress('Backup completed successfully!');
  } catch (error) {
    console.error('Backup error:', error);
    throw error;
  }
};

export const syncFromApi = async (onProgress) => {
  try {
    onProgress('Fetching Arabic text...');
    const resAr = await fetch(`${QURAN_API_BASE}/quran/quran-uthmani`);
    const dataAr = await resAr.json();

    onProgress('Fetching English translation...');
    const resEn = await fetch(`${QURAN_API_BASE}/quran/en.sahih`);
    const dataEn = await resEn.json();

    onProgress('Fetching Bengali translation...');
    const resBn = await fetch(`${QURAN_API_BASE}/quran/bn.bengali`);
    const dataBn = await resBn.json();

    const surahs = dataAr.data.surahs;
    const totalSurahs = surahs.length;



    for (let i = 0; i < totalSurahs; i++) {
      const surahAr = surahs[i];
      const surahEn = dataEn.data.surahs[i];
      const surahBn = dataBn.data.surahs[i];
      const surahNumber = surahAr.number.toString();

      onProgress(`Syncing Surah ${surahNumber} / ${totalSurahs} (${surahAr.englishName})`);

      const surahMeta = {
        number: surahAr.number,
        name: surahAr.name,
        englishName: surahAr.englishName,
        englishNameTranslation: surahAr.englishNameTranslation,
        revelationType: surahAr.revelationType,
        numberOfAyahs: surahAr.ayahs.length,
        surahImage: '',
        transliterationBn: '',
        transliterationEn: ''
      };

      // Save surah meta
      await db.collection('surahs').doc(surahNumber).set(surahMeta);

      // Save ayahs in subcollection
      let batch = db.batch();
      let opCount = 0;

      for (let j = 0; j < surahAr.ayahs.length; j++) {
        const ayahAr = surahAr.ayahs[j];
        const ayahEn = surahEn.ayahs[j];
        const ayahBn = surahBn.ayahs[j];

        const ayahDocRef = db.collection(`surahs/${surahNumber}/ayahs`).doc(ayahAr.numberInSurah.toString());

        const ayahData = {
          numberInSurah: ayahAr.numberInSurah,
          number: ayahAr.number,
          text: ayahAr.text,
          transliterationBn: '',
          transliterationEn: '',
          audio: [],
          translations: [
            { language: 'en', source: 'Sahih International', text: ayahEn.text },
            { language: 'bn', source: 'Muhiuddin Khan', text: ayahBn.text }
          ],
          tafsirs: []
        };

        batch.set(ayahDocRef, ayahData);
        opCount++;

        if (opCount >= 450) {
          await batch.commit();
          batch = db.batch();
          opCount = 0;
        }
      }

      if (opCount > 0) {
        await batch.commit();
      }


    }

    onProgress('Sync completed successfully!');

    // Also update the meta info to trigger app updates if it's already live
    await db.collection('quran_meta').doc('info').set({
      last_sync_time: firebase.firestore.FieldValue.serverTimestamp()
    }, { merge: true });

  } catch (error) {
    console.error('Sync error:', error);
    throw error;
  }
};
